import { Headline1, BodyRegular, FlexSmAlignCenter, FlexCol } from 'components/Elements';
import { DOCUMENTS_URL } from 'lib/_api-helpers';
import styled from 'styled-components';
import { theme, sizes, breakpoints } from 'theme';
import strings from '../../strings/strings.json';
import ChangeRoleButton from 'components/buttons/ChangeRoleButton';
import learnerBannerUrl from '../../assets/headerBanners/learner.svg';
import trainerBannerUrl from '../../assets/headerBanners/trainer.svg';
import orgBannerUrl from '../../assets/headerBanners/org.svg';
import { useContext } from 'react';
import { AuthContext } from 'contextProviders/AuthContext';
import { ROLE_DETAILS, RoleType } from 'models/User';

type HeaderProps = {
    title: string;
    textColourInverted?: boolean;
    logoS3Filepath?: string | null;
    roleSwitch?: boolean;
    maxWidth?: string;
    aboveTitleElement?: JSX.Element;
    rightTitleElement?: JSX.Element;
    learnerTabOverlap?: boolean;
};

export default function Header({
    title,
    logoS3Filepath = null,
    textColourInverted = true,
    roleSwitch = false,
    maxWidth,
    aboveTitleElement,
    rightTitleElement,
    learnerTabOverlap = false,
}: HeaderProps) {
    const { isAuthenticated, userData } = useContext(AuthContext);
    const userRole = isAuthenticated ? userData.activeRole : undefined;

    let bannerUrl = learnerBannerUrl as string;
    let textBackground = `${theme.learnerHeaderTextBackground}`;
    let backgroundColour = `${theme.learnerHeader}`;

    if (userRole) {
        switch (userRole) {
            case RoleType.TRAINER:
                bannerUrl = trainerBannerUrl as string;
                backgroundColour = `${theme.trainerHeader}`;
                textBackground = `${theme.trainerHeaderTextBackground}`;
                break;
            case RoleType.ORGANISATION_ADMIN:
                bannerUrl = orgBannerUrl as string;
                backgroundColour = `${theme.orgHeader}`;
                textBackground = `${theme.orgHeaderTextBackground}`;
                break;
            case RoleType.ADMIN:
                bannerUrl = '';
                backgroundColour = `${theme.adminHeader}`;
                textBackground = `${theme.textColour}`;
                break;
            default:
            case RoleType.LEARNER:
                break;
        }
    }

    const roleDetails = ROLE_DETAILS.find((x) => x.name === userRole);

    const friendlyName = roleDetails?.friendlyName ?? '';
    const icon = roleDetails?.icon ?? null;

    const titleRow = (
        <TitleRowContainer>
            <TextBackground $textBackground={textBackground}>
                {logoS3Filepath && (
                    <LogoContainer>
                        <LogoBackground>
                            <Image
                                src={`${DOCUMENTS_URL}/${logoS3Filepath}`}
                                alt={strings.organisationOverviewPage.logoAltText}
                            />
                        </LogoBackground>
                    </LogoContainer>
                )}
                <Headline1 $inverted={textColourInverted}>{title}</Headline1>
            </TextBackground>

            {rightTitleElement && rightTitleElement}

            {roleSwitch && isAuthenticated && (
                <ButtonContainer>
                    <FlexSmAlignCenter>
                        {icon}
                        <BodyRegular $bold>{friendlyName}</BodyRegular>
                    </FlexSmAlignCenter>
                    <ChangeRoleButton />
                </ButtonContainer>
            )}
        </TitleRowContainer>
    );

    return (
        <HeaderBackground
            $bannerUrl={bannerUrl}
            $backgroundColour={backgroundColour}
            $learnerTabOverlap={learnerTabOverlap}
        >
            <LimitedContainer $maxWidth={maxWidth}>
                {aboveTitleElement && aboveTitleElement}
                {titleRow}
            </LimitedContainer>
        </HeaderBackground>
    );
}

const HeaderBackground = styled.header<{ $bannerUrl: string; $backgroundColour: string; $learnerTabOverlap: boolean }>`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    // colour gradient fallback sits under SVG
    background-image: url(${({ $bannerUrl }) => $bannerUrl}), ${({ $backgroundColour }) => $backgroundColour};
    min-height: 200px;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;

    // allow learner module tabs to overlap header
    padding-bottom: ${({ $learnerTabOverlap }) => {
        return $learnerTabOverlap ? `${sizes.learnerTabsHeight}` : '0';
    }};
    margin-bottom: ${({ $learnerTabOverlap }) => {
        return $learnerTabOverlap ? `-${sizes.learnerTabsHeight}` : '0';
    }};
`;

const LimitedContainer = styled.div<{ $maxWidth?: string }>`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    gap: ${sizes.spacingMd};
    max-width: ${({ $maxWidth }) => ($maxWidth ? $maxWidth : `${sizes.appMaxWidthWithoutPadding}px`)};
    padding: ${sizes.spacingLg} ${sizes.edgePadding};

    @media (max-width: ${breakpoints.sm}) {
        padding: ${sizes.spacingMd} ${sizes.edgePadding};
    }
`;

const TitleRowContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${sizes.spacingMd};

    @media (max-width: ${breakpoints.md}) {
        flex-direction: column-reverse;
        align-items: unset;
    }
`;

const TextBackground = styled.div<{ $textBackground: string }>`
    background: ${({ $textBackground }) => $textBackground};
    border-radius: ${sizes.spacingRg};
    display: flex;
    padding: ${sizes.spacingMd};
    gap: ${sizes.spacingMd};
    align-items: center;
    width: fit-content;

    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        align-items: unset;
        gap: ${sizes.spacingSm};
    }
`;

const ButtonContainer = styled(FlexCol)`
    background: rgba(255, 255, 255, 1);
    box-shadow: 0px 4px 19.9px 0px rgba(0, 0, 0, 0.06);
    padding: ${sizes.spacingSm};
    border-radius: ${sizes.borderRadiusLg};
    width: fit-content;
    gap: ${sizes.spacingSm};
`;

const LogoContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const LogoBackground = styled.div`
    background: ${theme.cardBackground};
    height: auto;
    width: auto;
    max-width: fit-content;
    max-height: fit-content;
    padding: ${sizes.spacingSm};
    border-radius: ${sizes.borderRadiusXl};
`;

const Image = styled.img`
    border-radius: ${sizes.borderRadiusMd};
    max-width: 244px;
    max-height: 88px;
    width: auto;
    height: auto;
    object-fit: contain;
`;
